<script setup lang="ts">
import { computed } from 'vue';
import type Steps from '@/interfaces/Steps';
import ConnxIcon from '../Atoms/ConnxIcon.vue';
import ConnxProgressBar from '../Atoms/ConnxProgressBar.vue';

interface Props {
  steps : Steps[];
  currentStep : number;
}

const props = defineProps<Props>();

const progressSteps = computed(() => props.steps.map((step, index) => ({
  ...step,
  active: props.currentStep === index + 1,
  count: index + 1,
})));

const backgroundSliderPosition = computed(
  () => (props.currentStep < 1 ? 0 : (props.currentStep - 1) / (props.steps.length - 1)) * 100,
);

const lastStep = computed(() => props.steps.length === props.currentStep);
</script>

<template>
  <div
    class="tw-flex tw-justify-between tw-space-x-4 tw-relative"
  >
    <div
      class="
        tw-absolute
        tw-left-20
        tw-right-20
        sm:tw-left-36
        sm:tw-right-36
        tw-h-1
        tw-top-7
        tw-z-0
      "
    >
      <connx-progress-bar
        :percentage="backgroundSliderPosition"
        background-color="tw-bg-connx-success"
        background-fill="tw-bg-connx-grey-tint-70"
      />
    </div>
    <div class="Steps">
      <div
        v-for="(step, index) in progressSteps"
        :key="index"
        class="tw-z-1 tw-relative"
      >
        <div
          class="
            tw-rounded-full
            tw-text-2xl
            tw-leading-none
            tw-w-14
            tw-h-14
            tw-mx-auto
            tw-flex
            tw-items-center
            tw-transition
            tw-ease-in-out
            tw-delay-150
            tw-justify-center
            tw-z-10
          "
          :class="[{
            'tw-bg-connx-grey-tint-70 tw-text-connx-white': step.count > currentStep,
            'tw-bg-connx-success tw-text-connx-white': lastStep || step.count < currentStep,
            'tw-border-connx-success tw-border-solid tw-border tw-bg-connx-white tw-text-connx-success':
              step.active && !lastStep,
          }, step.cssOverrides]"
        >
          <span v-if="step.icon">
            <connx-icon
              v-if="step.iconActive && step.active"
              :name="step.iconActive"
              class="tw-w-6 tw-items-center"
            />
            <connx-icon
              v-else
              :name="step.icon"
              class="tw-w-6 tw-items-center"
            />
          </span>
          <span v-else>
            {{ step.count }}
          </span>
        </div>
        <h3 :class="['tw-text-center tw-font-semibold tw-text-connx-grey-shade-20 tw-mt-3 tw-text-xl', step.cssTextOverrides]">
          {{ step.title }}
        </h3>
      </div>
    </div>
  </div>
</template>

<style lang="css" scoped>
.Steps {
  display: grid;
  grid-template-columns: repeat(v-bind('steps.length'), minmax(0, 1fr));
  width: 100%;
  margin-left: 0 !important;
}
</style>
